import { AuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { ContainerDefault, ErrorMessage, ErrorMessagePage, Heading } from '@core/ui';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingPanel } from './BillingPanel';
import { UsageBarChart } from './UsageBarChart';
import { UsagePanel } from './UsagePanel';

type Preset = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear';

// Shared by the range tiles and the "today so far" tiles, so the two blocks cannot come to show
// different metrics.
//
// Two labels name a unit rather than a person, and deliberately. `ondemand_viewers` counts
// SESSIONS, and the player opens a new one on every backward seek and every forward jump over ten
// seconds - one person scrubbing fifteen times through one recording is sixteen of them, so
// "On-demand viewers" was a headcount the number could not support on the customer's own page.
// `live_viewers` counts participations, one per person per webinar, so the same person attending
// three webinars is three. The operator UI prints each metric's unit beside it; this page does
// not, so the label has to carry it.
const headlineMetrics: [key: string, labelKey: string][] = [
    ['live_webinars', 'UsageStatistics.Metric.LiveWebinars'],
    ['test_webinars', 'UsageStatistics.Metric.TestWebinars'],
    ['webinar_minutes', 'UsageStatistics.Metric.WebinarMinutes'],
    ['live_viewers', 'UsageStatistics.Metric.LiveViewers'],
    ['live_watch_minutes', 'UsageStatistics.Metric.LiveWatchMinutes'],
    ['ondemand_viewers', 'UsageStatistics.Metric.OnDemandViewers'],
    ['ondemand_watch_minutes', 'UsageStatistics.Metric.OnDemandWatchMinutes'],
    ['ondemand_webinars_total', 'UsageStatistics.Metric.OnDemandCatalogue'],
    ['storage_bytes_total', 'UsageStatistics.Metric.StorageBytes']
];

const presetRange = (preset: Preset): { from: string; to: string } => {
    const now = DateTime.local();
    const iso = (d: DateTime) => d.toISODate()!;
    switch (preset) {
        case 'lastMonth': {
            const start = now.minus({ months: 1 }).startOf('month');
            return { from: iso(start), to: iso(start.endOf('month')) };
        }
        case 'thisQuarter':
            return { from: iso(now.startOf('quarter')), to: iso(now) };
        case 'thisYear':
            return { from: iso(now.startOf('year')), to: iso(now) };
        default:
            return { from: iso(now.startOf('month')), to: iso(now) };
    }
};

export const ManageUsageStatisticsPage = () => {
    const canViewUsageStatistics = useContext(AuthContext).canViewUsageStatistics;
    const { t } = useTranslation();

    const [range, setRange] = useState(() => presetRange('thisMonth'));

    // `range` is the single source of truth; the active preset (if any) is derived from it rather
    // than tracked separately, so editing a date input can never leave a stale preset button
    // showing as pressed for a range it no longer describes.
    const activePreset = (['thisMonth', 'lastMonth', 'thisQuarter', 'thisYear'] as Preset[]).find((p) => {
        const r = presetRange(p);
        return r.from === range.from && r.to === range.to;
    });

    // Ranges are ISO dates, which compare lexicographically, so this is the whole test.
    const todayIso = DateTime.local().toISODate()!;
    const rangeIncludesToday = range.from <= todayIso && todayIso <= range.to;

    const { data: summary, isLoading: isSummaryLoading, error: summaryError } = useQuery(OptionsApi.usageStatistics.range(range.from, range.to));
    const { data: days, isLoading: isDaysLoading, error: daysError } = useQuery(OptionsApi.usageStatistics.daily(range.from, range.to));

    // Only fetched where it is part of the answer. On "Last month" today is not in the range, and
    // asking for it produced a provisional caption about a day the reader is not looking at.
    const { data: today, isLoading: isTodayLoading, error: todayError } = useQuery(OptionsApi.usageStatistics.today({ enabled: rangeIncludesToday }));

    // Fetched through the authenticated client rather than linked: the endpoint is [Authorize]d
    // and a link navigation carries no bearer token.
    const exportRange = useMutation(OptionsApi.usageStatistics.exportRange());

    // Weekly buckets: the chart is coarse on purpose, and a day-per-bar chart over a year is
    // unreadable anyway. The export is where per-day detail lives.
    const buckets = useMemo(() => {
        if (!days) return [];
        const byWeek = new Map<string, number>();
        for (const row of days) {
            const week = DateTime.fromISO(row.day).startOf('week').toISODate()!;
            byWeek.set(week, (byWeek.get(week) ?? 0) + (row.metrics['live_webinars'] ?? 0));
        }
        return [...byWeek.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([label, value]) => ({ label, value }));
    }, [days]);

    if (!canViewUsageStatistics) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const value = (key: string) => summary?.values[key] ?? 0;

    // Today is never frozen, so a range ending today always reports it as a day without data.
    // Counting it as missing while its figures are on the screen is two captions contradicting
    // each other.
    const daysMissing = Math.max(0, (summary?.daysMissing ?? 0) - (rangeIncludesToday && today ? 1 : 0));

    // The page frame — heading, range controls, export — always renders. Each block below reports
    // its own loading and failure through UsagePanel, so one failing request no longer takes the
    // date controls down with it.
    return (
        <ContainerDefault>
            <BackLink to="/manage" />
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('UsageStatistics.PageHeading')}
            </Heading>

            <div className="flex flex-wrap gap-2 my-4">
                {(['thisMonth', 'lastMonth', 'thisQuarter', 'thisYear'] as Preset[]).map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => setRange(presetRange(p))}
                        aria-pressed={activePreset === p}
                        className={activePreset === p ? 'underline font-semibold' : 'underline'}
                    >
                        {t(`UsageStatistics.Range.${p.charAt(0).toUpperCase()}${p.slice(1)}`)}
                    </button>
                ))}
                <input
                    type="date"
                    value={range.from}
                    onChange={(e) => setRange({ ...range, from: e.target.value })}
                    aria-label={t('UsageStatistics.Filter.From')}
                />
                <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} aria-label={t('UsageStatistics.Filter.To')} />
                <button type="button" onClick={() => exportRange.mutate(range)} disabled={exportRange.isPending} className="underline">
                    {t('UsageStatistics.Export')}
                </button>
            </div>

            {exportRange.error && <ErrorMessage apiError={exportRange.error} classNames="my-4" />}

            <UsagePanel isLoading={isSummaryLoading} error={summaryError}>
                {/* Caveats first: a reader who does not know the range is incomplete will read a
                    short range as a quiet one. */}
                {daysMissing > 0 && <p className="opacity-70">{t('UsageStatistics.DaysMissing', { count: daysMissing })}</p>}
                {summary?.containsApproximateOnDemandDates && <p className="opacity-70">{t('UsageStatistics.ApproximateOnDemand')}</p>}
                {summary && !summary.hasStockBaseline && <p className="opacity-70">{t('UsageStatistics.NoStockBaseline')}</p>}

                <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
                    {headlineMetrics.map(([key, labelKey]) => (
                        <div key={key}>
                            <dt className="text-sm opacity-70">{t(labelKey)}</dt>
                            <dd className="text-2xl">{Math.round(value(key)).toLocaleString()}</dd>
                            {/* Suppressed without a baseline day: the delta would then be the whole
                                standing level presented as growth inside this range. */}
                            {summary?.hasStockBaseline && summary.stockDeltas[key] !== undefined && (
                                <dd className="text-xs opacity-70">
                                    {t('UsageStatistics.StockDelta', {
                                        change: `${summary.stockDeltas[key] >= 0 ? '+' : ''}${Math.round(summary.stockDeltas[key]).toLocaleString()}`
                                    })}
                                </dd>
                            )}
                        </div>
                    ))}
                </dl>
            </UsagePanel>

            <UsagePanel isLoading={isDaysLoading} error={daysError}>
                {buckets.length === 0 ? (
                    // An empty chart and a chart that never loaded looked identical before; say
                    // which this is.
                    <p className="opacity-70 my-4">{t('UsageStatistics.Chart.NoData')}</p>
                ) : (
                    <UsageBarChart buckets={buckets} ariaLabel={t('UsageStatistics.Chart.LiveWebinarsPerWeek')} />
                )}
            </UsagePanel>

            {/* Today is shown, as spec §7 requires, but beside the range rather than inside it.
                Adding it to the totals would make the same range answer differently on every
                refresh, and would put figures on the screen that the Excel export — which carries
                frozen days only — does not contain, on the one page whose purpose is reconciling
                against an invoice. A block of its own says which numbers still move. */}
            {rangeIncludesToday && (
                <section className="my-8 border-t pt-6">
                    <h2 className="text-xl font-semibold">{t('UsageStatistics.Today.Heading')}</h2>
                    <p className="opacity-70">{t('UsageStatistics.Provisional')}</p>
                    <UsagePanel isLoading={isTodayLoading} error={todayError}>
                        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
                            {headlineMetrics.map(([key, labelKey]) => (
                                <div key={key}>
                                    <dt className="text-sm opacity-70">{t(labelKey)}</dt>
                                    <dd className="text-2xl">{Math.round(today?.metrics[key] ?? 0).toLocaleString()}</dd>
                                </div>
                            ))}
                        </dl>
                    </UsagePanel>
                </section>
            )}

            <BillingPanel from={range.from} to={range.to} />
        </ContainerDefault>
    );
};
